import dayjs from "@eisox/dayjs";
import { ChevronLeftIcon, ChevronRightIcon } from "@eisox/icons";

import { cn } from "../..";
import { useLocale } from "../../Locale";
import type { CalendarViewProps, PeriodViewOptions } from "../date-field.type";

const CELL_COUNT = 12;

const NAVIGATION_LABELS = {
  fr: { previous: "Précédent", next: "Suivant" },
  en: { previous: "Previous", next: "Next" },
};

const NAVIGATION_CLASS_NAME =
  "inline-flex h-9 w-9 cursor-pointer items-center justify-center text-primary-blue disabled:cursor-default disabled:opacity-50";

interface PeriodViewProps extends CalendarViewProps, PeriodViewOptions {
  unit: "month" | "year";
}

const PeriodView = ({
  unit,
  displayed,
  onDisplayedChange,
  onPeriodClick,
  onZoomOut,
  selection,
  min,
  max,
  className,
}: PeriodViewProps) => {
  const locale = useLocale();
  const pageYears = unit === "month" ? 1 : CELL_COUNT;
  const first = displayed.year(Math.floor(displayed.year() / pageYears) * pageYears).startOf("year");
  const last = first.add(CELL_COUNT - 1, unit);
  const cells = Array.from({ length: CELL_COUNT }, (_, offset) => first.add(offset, unit));
  const title = unit === "month" ? first.format("YYYY") : `${first.year()} – ${last.year()}`;
  const { from, to } = selection.mode === "single" ? { from: selection.value, to: selection.value } : selection.value;
  const isBanded = selection.mode === "range" && !!from && !!to;

  return (
    <div className={cn("w-[308px] text-base text-primary-dark-blue", className)}>
      <div className="h-11 font-medium flex items-center justify-between">
        {onZoomOut ? (
          <button type="button" className="cursor-pointer hover:text-primary-blue" onClick={onZoomOut}>
            {title}
          </button>
        ) : (
          <span>{title}</span>
        )}
        <div className="flex items-center">
          <button
            type="button"
            aria-label={NAVIGATION_LABELS[locale].previous}
            className={NAVIGATION_CLASS_NAME}
            disabled={!!min && !min.isBefore(first)}
            onClick={() => onDisplayedChange(first.subtract(pageYears, "year"))}
          >
            <ChevronLeftIcon />
          </button>
          <button
            type="button"
            aria-label={NAVIGATION_LABELS[locale].next}
            className={NAVIGATION_CLASS_NAME}
            disabled={!!max && max.isBefore(first.add(pageYears, "year"))}
            onClick={() => onDisplayedChange(first.add(pageYears, "year"))}
          >
            <ChevronRightIcon />
          </button>
        </div>
      </div>
      <div className="gap-y-2 grid grid-cols-3">
        {cells.map(cell => {
          const isStart = !!from && cell.isSame(from, unit);
          const isEnd = !!to && cell.isSame(to, unit);
          const isInside = !!from && !!to && cell.isAfter(from, unit) && cell.isBefore(to, unit);
          const isDisabled = (!!min && cell.isBefore(min, unit)) || (!!max && cell.isAfter(max, unit));
          return (
            <div
              key={cell.toISOString()}
              className={cn(
                isBanded && (isStart || isEnd || isInside) && "bg-primary-blue/8",
                isBanded && isStart && "rounded-l-full",
                isBanded && isEnd && "rounded-r-full",
              )}
            >
              <button
                type="button"
                disabled={isDisabled}
                aria-pressed={isStart || isEnd}
                className={cn(
                  "h-11 w-full cursor-pointer rounded-full text-center capitalize disabled:cursor-default disabled:opacity-50",
                  cell.isSame(dayjs(), unit) && "text-primary-blue",
                  (isStart || isEnd) && "font-semibold bg-primary-blue text-primary-white",
                )}
                onClick={() => onPeriodClick(cell)}
              >
                {unit === "month" ? cell.locale(locale).format("MMM") : cell.year()}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

PeriodView.displayName = "PeriodView";

export { PeriodView };
